import { ScanLine, Trophy, PackageOpen, Users, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { PageHeader } from '@/components/dashboard/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { Button } from '@/components/ui/button';
import { campaigns, validations, fmt } from '@/lib/mockData';
import { cn } from '@/lib/utils';

const activeCampaign = campaigns.find(c => c.id === 'cmp_didi_b')!;

const statusIcon = {
  validated: <CheckCircle2 className="size-3.5 text-emerald-600" />,
  pending: <Clock className="size-3.5 text-amber-600" />,
  expired: <XCircle className="size-3.5 text-muted-foreground" />,
};
const statusLabel = { validated: 'Validé', pending: 'En attente', expired: 'Expiré' };

const HotesseDashboard = () => (
  <DashboardLayout
    role="hotesse"
    user={{ name: 'Anike Aya', subtitle: 'Hôtesse Admin', initials: 'AA' }}
  >
    <PageHeader
      title="Bonjour Anike 👋"
      subtitle="Voici l'activité en direct de ta campagne assignée."
      actions={
        <Button size="lg" asChild className="shadow-cta">
          <Link to="/app/scanner">
            <ScanLine className="size-4" />
            Scanner un participant
          </Link>
        </Button>
      }
    />

    <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            Campagne active
          </p>
          <h2 className="mt-1 font-display text-xl font-bold">{activeCampaign.name}</h2>
          <p className="text-sm text-muted-foreground">
            {activeCampaign.brand} • {activeCampaign.city} • Roulette
          </p>
        </div>
        <StatusBadge status={activeCampaign.status} />
      </div>
    </div>

    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Participations"
        value={fmt.number(activeCampaign.participations)}
        delta={{ value: '+184 dernière heure', positive: true }}
        icon={Users}
        accent="accent"
      />
      <StatCard
        label="Lots remis"
        value={fmt.number(activeCampaign.prizesGiven)}
        delta={{ value: '+12 dernière heure', positive: true }}
        icon={Trophy}
        accent="primary"
      />
      <StatCard
        label="Lots restants"
        value={fmt.number(activeCampaign.prizesRemaining).padStart(2, '0')}
        icon={PackageOpen}
        accent="emerald"
      />
      <StatCard
        label="Taux de gain"
        value={fmt.pct(activeCampaign.winRate)}
        delta={{ value: 'Stable sur 24 h', positive: true }}
        icon={Trophy}
        accent="amber"
      />
    </div>

    <div className="mt-8 rounded-2xl border border-border bg-card shadow-soft">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <h3 className="font-display text-lg font-bold">Validations récentes</h3>
          <p className="text-xs text-muted-foreground">
            Dernières interactions de ton équipe sur le terrain.
          </p>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/historique">Voir tout</Link>
        </Button>
      </div>
      <ul className="divide-y divide-border">
        {validations.slice(0, 5).map(v => (
          <li key={v.id} className="flex items-center gap-4 px-5 py-3">
            <div className="grid size-9 place-items-center rounded-full bg-muted font-display text-xs font-bold text-foreground">
              {v.participantName
                .split(' ')
                .map(n => n[0])
                .join('')
                .slice(0, 2)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{v.participantName}</p>
              <p className="truncate text-xs text-muted-foreground">{v.prize}</p>
            </div>
            <code className="hidden rounded-md bg-muted px-2 py-1 font-mono text-xs text-foreground sm:block">
              {v.code}
            </code>
            <span className="hidden text-xs text-muted-foreground sm:block">{v.time}</span>
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                v.status === 'validated' &&
                  'border-emerald-500/30 bg-emerald-500/10 text-emerald-700',
                v.status === 'pending' && 'border-amber-500/30 bg-amber-500/10 text-amber-700',
                v.status === 'expired' && 'border-border bg-muted text-muted-foreground'
              )}
            >
              {statusIcon[v.status]}
              {statusLabel[v.status]}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </DashboardLayout>
);

export default HotesseDashboard;
